import { X, RotateCcw, Trash2 } from 'lucide-react'
import type { OfflineQueueItem } from '@/shared/lib/dexie'
import { Button } from '@/shared/components/ui/button'
import { formatCurrency } from '@/shared/lib/utils'

const PROCEDURE_LABELS: Record<string, string> = {
  'orders.complete': 'Sale',
  'restock.complete': 'Restock',
  'adjustment.complete': 'Adjustment',
}

interface Props {
  items: OfflineQueueItem[]
  currency: string
  onRetry: (id: string) => void
  onDiscard: (id: string) => void
  onClose: () => void
}

function fmtDate(iso: string) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso))
}

export function OfflineFailedSheet({ items, currency, onRetry, onDiscard, onClose }: Props) {
  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Items that couldn't be saved">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Sheet — bottom on mobile, centered dialog on sm+ */}
      <div className="absolute inset-x-0 bottom-0 max-h-[80dvh] overflow-y-auto rounded-t-2xl border-t border-border bg-background shadow-xl sm:inset-x-auto sm:left-1/2 sm:bottom-auto sm:top-1/2 sm:w-full sm:max-w-md sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:border">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-border bg-background px-4 py-3">
          <div>
            <h2 className="font-heading text-sm font-semibold text-foreground">
              Items that couldn&apos;t be saved
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {items.length} {items.length === 1 ? 'item' : 'items'} — retry to try again, or discard to remove
            </p>
          </div>
          <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close">
            <X />
          </Button>
        </div>

        {/* Item list */}
        <ul className="divide-y divide-border">
          {items.map(item => {
            const label = PROCEDURE_LABELS[item.procedure] ?? item.procedure
            const input = item.input as { total?: number }
            const total = typeof input.total === 'number' ? input.total : null

            return (
              <li key={item.id} className="flex items-start gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">
                    {label}
                    {total !== null && (
                      <span className="ml-1.5 font-normal text-muted-foreground">
                        · {formatCurrency(total, currency)}
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{fmtDate(item.created_at)}</p>
                  {item.error_message && (
                    <p className="mt-1 line-clamp-2 text-xs text-destructive">{item.error_message}</p>
                  )}
                </div>
                <div className="flex shrink-0 gap-1.5 pt-0.5">
                  <Button
                    variant="outline"
                    size="xs"
                    data-testid={`retry-${item.id}`}
                    onClick={() => onRetry(item.id)}
                  >
                    <RotateCcw />
                    Retry
                  </Button>
                  <Button
                    variant="destructive"
                    size="xs"
                    onClick={() => onDiscard(item.id)}
                  >
                    <Trash2 />
                    Discard
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
